/**
 * CartEvents.js
 * Handles all event binding and interactions for cart notepad
 */

import { CartCalculations } from './CartCalculations.js';

export class CartEvents {
  /**
   * Bind events for standard cart notepad
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Object} context - Event context
   */
  static bindEvents(notepad, context) {
    const { hideCallback, pendingOrder } = context;

    this.bindCloseButton(notepad, hideCallback);
    this.bindViewFullOrderButton(notepad, hideCallback);
    this.bindContinuePaymentButton(notepad, hideCallback, pendingOrder);
    this.bindCancelOrderButton(notepad, hideCallback, pendingOrder);
    this.bindConfirmPendingButton(notepad, hideCallback);
    this.bindOutsideClick(notepad, hideCallback);
  }

  /**
   * Bind events for unpaid orders view
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Object} context - Event context
   */
  static bindUnpaidEvents(notepad, context) {
    const { hideCallback, unpaidOrders } = context;

    this.bindCloseButton(notepad, hideCallback);
    this.bindToggleButtons(notepad);
    this.bindPayAllButton(notepad, hideCallback, unpaidOrders);
  }

  /**
   * Bind events for combined cart and unpaid orders view
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Object} context - Event context
   */
  static bindCombinedEvents(notepad, context) {
    const { hideCallback, cartItems, unpaidOrders } = context;

    this.bindConfirmCartButton(notepad, hideCallback, cartItems);
    this.bindUnpaidEvents(notepad, { hideCallback, unpaidOrders });
  }

  /**
   * Bind close button click event
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Function} hideCallback - Hide callback function
   */
  static bindCloseButton(notepad, hideCallback) {
    const closeBtn = notepad.querySelector('.notepad-close');
    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        hideCallback();
      });

      closeBtn.addEventListener('mouseenter', () => {
        closeBtn.style.backgroundColor = '#f0f0f0';
      });

      closeBtn.addEventListener('mouseleave', () => {
        closeBtn.style.backgroundColor = 'transparent';
      });
    }
  }

  /**
   * Bind view full order button click event
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Function} hideCallback - Hide callback function
   */
  static bindViewFullOrderButton(notepad, hideCallback) {
    const viewBtn = notepad.querySelector('.view-full-order-btn');
    if (viewBtn) {
      viewBtn.addEventListener('click', () => {
        const event = new CustomEvent('showOrderConfirmation', { detail: { source: 'notepad' } });
        document.dispatchEvent(event);
        hideCallback();
      });

      viewBtn.addEventListener('mouseenter', () => {
        viewBtn.style.transform = 'translateY(-1px)';
        viewBtn.style.boxShadow = '0 4px 12px rgba(46, 90, 61, 0.3)';
      });

      viewBtn.addEventListener('mouseleave', () => {
        viewBtn.style.transform = 'translateY(0)';
        viewBtn.style.boxShadow = 'none';
      });
    }
  }

  /**
   * Bind continue payment button click event
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Function} hideCallback - Hide callback function
   * @param {Object} pendingOrder - Pending order data
   */
  static bindContinuePaymentButton(notepad, hideCallback, pendingOrder) {
    const continuePaymentBtn = notepad.querySelector('.continue-payment-btn');
    if (continuePaymentBtn) {
      continuePaymentBtn.addEventListener('click', () => {
        const event = new CustomEvent('continuePayment', {
          detail: { order: pendingOrder }
        });
        document.dispatchEvent(event);
        hideCallback();
      });
    }
  }

  /**
   * Bind cancel order button click event
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Function} hideCallback - Hide callback function
   * @param {Object} pendingOrder - Pending order data
   */
  static bindCancelOrderButton(notepad, hideCallback, pendingOrder) {
    const cancelOrderBtn = notepad.querySelector('.cancel-order-btn');
    if (cancelOrderBtn) {
      cancelOrderBtn.addEventListener('click', () => {
        const event = new CustomEvent('cancelPendingOrder', {
          detail: { order: pendingOrder }
        });
        document.dispatchEvent(event);
        hideCallback();
      });
    }
  }

  /**
   * Bind confirm pending button click event
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Function} hideCallback - Hide callback function
   */
  static bindConfirmPendingButton(notepad, hideCallback) {
    const confirmPendingBtn = notepad.querySelector('.confirm-pending-btn');
    if (confirmPendingBtn) {
      confirmPendingBtn.addEventListener('click', () => {
        const event = new CustomEvent('showOrderConfirmation', { detail: { source: 'notepad-pending' } });
        document.dispatchEvent(event);
        hideCallback();
      });
    }
  }

  /**
   * Bind confirm cart button click event (combined view)
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Function} hideCallback - Hide callback function
   * @param {Array} cartItems - Cart items array
   */
  static bindConfirmCartButton(notepad, hideCallback, cartItems) {
    const confirmCartBtn = notepad.querySelector('.confirm-cart-btn');
    if (confirmCartBtn) {
      confirmCartBtn.addEventListener('click', () => {
        const totals = CartCalculations.calculateTotals(cartItems);
        const evt = new CustomEvent('confirmCartOrder', {
          detail: { items: cartItems, totals }
        });
        document.dispatchEvent(evt);
        hideCallback();
      });
    }
  }

  /**
   * Bind toggle buttons for unpaid orders
   * @param {HTMLElement} notepad - Notepad DOM element
   */
  static bindToggleButtons(notepad) {
    notepad.querySelectorAll('.toggle-btn').forEach(btn => {
      btn.addEventListener('click', (e) => {
        const id = e.currentTarget.getAttribute('data-toggle');
        const body = notepad.querySelector(`.unpaid-order[data-order-id="${id}"] .unpaid-body`);
        if (!body) return;

        const visible = body.style.display !== 'none';
        body.style.display = visible ? 'none' : 'block';
        e.currentTarget.innerHTML = `<i class="fas ${visible ? 'fa-chevron-down' : 'fa-chevron-up'}"></i>`;
      });
    });
  }

  /**
   * Bind pay all button click event
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Function} hideCallback - Hide callback function
   * @param {Array} unpaidOrders - Unpaid orders array
   */
  static bindPayAllButton(notepad, hideCallback, unpaidOrders) {
    const payAll = notepad.querySelector('.pay-all-btn');
    if (payAll) {
      payAll.addEventListener('click', () => {
        const ids = unpaidOrders.map(o => o.id);
        const event = new CustomEvent('payAllUnpaidOrders', { detail: { orderIds: ids } });
        document.dispatchEvent(event);
        hideCallback();
      });
    }
  }

  /**
   * Bind outside click to hide notepad
   * @param {HTMLElement} notepad - Notepad DOM element
   * @param {Function} hideCallback - Hide callback function
   */
  static bindOutsideClick(notepad, hideCallback) {
    const handleOutsideClick = (e) => {
      if (!notepad.contains(e.target)) {
        hideCallback();
        document.removeEventListener('click', handleOutsideClick);
      }
    };

    setTimeout(() => {
      document.addEventListener('click', handleOutsideClick);
    }, 100);
  }
}
